"use client";

import React from "react";
import { Mail, MapPin, Phone, Twitter, Linkedin, Github, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
const socialLinks = [{
  icon: Twitter,
  href: "https://twitter.com",
  label: "Twitter"
}, {
  icon: Linkedin,
  href: "https://linkedin.com",
  label: "LinkedIn"
}, {
  icon: Github,
  href: "https://github.com",
  label: "GitHub"
}];
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="h-full flex flex-col items-center justify-center text-center p-8 border border-white/10 bg-white/5 rounded-lg animate-in fade-in zoom-in duration-500">
        <h3 className="text-2xl font-semibold text-white mb-2">Message Sent</h3>
        <p className="text-zinc-400 mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-white/20 text-white hover:bg-white hover:text-black transition-colors">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-zinc-400">Your Name</FormLabel>
            <FormControl>
              <Input placeholder="John Doe" {...field} className="border-0 border-b border-white/20 rounded-none bg-transparent px-0 py-2 text-white placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:border-[var(--primary)] shadow-none transition-colors" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-zinc-400">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" {...field} className="border-0 border-b border-white/20 rounded-none bg-transparent px-0 py-2 text-white placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:border-[var(--primary)] shadow-none transition-colors" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="subject" rules={{
      required: "Subject is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-zinc-400">Subject</FormLabel>
            <FormControl>
              <Input placeholder="Brief summary of your inquiry" {...field} className="border-0 border-b border-white/20 rounded-none bg-transparent px-0 py-2 text-white placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:border-[var(--primary)] shadow-none transition-colors" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-zinc-400">Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us how we can help you..." {...field} className="min-h-[120px] border-0 border-b border-white/20 rounded-none bg-transparent px-0 py-2 text-white placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:border-[var(--primary)] shadow-none resize-none transition-colors" />
            </FormControl>
            <FormDescription className="text-zinc-600">
              Provide as much detail as possible
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="newsletter_opt_in" render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0">
            <FormControl>
              <input type="checkbox" checked={field.value} onChange={field.onChange} className="size-4 mt-1 rounded border-white/20 bg-transparent text-[var(--primary)] focus:ring-[var(--primary)] focus:ring-offset-0 accent-[var(--primary)]" />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-zinc-400 font-normal">
                Subscribe to our newsletter
              </FormLabel>
              <FormDescription className="text-zinc-600">
                Receive updates on the latest AI tools and offers
              </FormDescription>
            </div>
          </FormItem>} />

      {submitError && <div className="p-3 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 bg-black text-white border border-white hover:bg-white hover:text-black transition-all duration-300 uppercase tracking-wider text-sm font-medium rounded-none cursor-pointer">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
}
export default function ReachOut() {
  return <section id="reach-out" className="dark bg-background text-foreground py-20 lg:py-32 px-4 md:px-8 overflow-hidden relative">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-[var(--primary)]/5 rounded-full blur-[100px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-[var(--secondary)]/5 rounded-full blur-[100px]" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {}
          <div className="flex flex-col justify-center space-y-12">
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white">
                Get in Touch
              </h2>
              <p className="text-lg text-zinc-400 max-w-md leading-relaxed">
                Have questions about AI tools or partnership opportunities? We're here to help you navigate the future of automation.
              </p>
            </div>

            <div className="space-y-8">
              <div className="flex items-start space-x-4 group">
                <div className="p-3 rounded-full bg-white/5 border border-white/10 group-hover:border-[var(--primary)]/50 group-hover:bg-[var(--primary)]/10 transition-colors duration-300">
                  <Mail className="size-6 text-zinc-300 group-hover:text-[var(--primary)] transition-colors" />
                </div>
                <div>
                  <h4 className="text-sm font-medium text-zinc-500 uppercase tracking-wider mb-1">Email Us</h4>
                  <Link to="mailto:contact@aiaffiliate.com" className="text-xl text-white hover:text-[var(--primary)] transition-colors">
                    contact@aiaffiliate.com
                  </Link>
                </div>
              </div>

              <div className="flex items-start space-x-4 group">
                <div className="p-3 rounded-full bg-white/5 border border-white/10 group-hover:border-[var(--primary)]/50 group-hover:bg-[var(--primary)]/10 transition-colors duration-300">
                  <MapPin className="size-6 text-zinc-300 group-hover:text-[var(--primary)] transition-colors" />
                </div>
                <div>
                  <h4 className="text-sm font-medium text-zinc-500 uppercase tracking-wider mb-1">Visit Us</h4>
                  <p className="text-xl text-white">
                    123 AI Avenue, Tech City, 45678
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 group">
                <div className="p-3 rounded-full bg-white/5 border border-white/10 group-hover:border-[var(--primary)]/50 group-hover:bg-[var(--primary)]/10 transition-colors duration-300">
                  <Phone className="size-6 text-zinc-300 group-hover:text-[var(--primary)] transition-colors" />
                </div>
                <div>
                  <h4 className="text-sm font-medium text-zinc-500 uppercase tracking-wider mb-1">Call Us</h4>
                  <Link to="tel:+11234567890" className="text-xl text-white hover:text-[var(--primary)] transition-colors">
                    (123) 456-7890
                  </Link>
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-white/10">
              <h4 className="text-sm font-medium text-zinc-500 uppercase tracking-wider mb-6">Follow Our Updates</h4>
              <div className="flex space-x-4">
                {socialLinks.map((social, i) => <Link key={i} to={social.href} newTab className="p-3 rounded-full bg-white/5 border border-white/10 text-zinc-400 hover:text-white hover:bg-white/10 hover:border-white/30 transition-all duration-300" aria-label={social.label} data-index={i}>
                    <social.icon className="size-5" />
                  </Link>)}
              </div>
            </div>
          </div>

          {}
          <div className="lg:pl-8">
            <div className="bg-zinc-900/30 backdrop-blur-sm border border-white/5 p-8 md:p-10 rounded-xl shadow-2xl">
              <FormProvider formId="reach_out_form_contact" sectionName="reach-out" defaultValues={{
              name: "",
              email: "",
              subject: "",
              message: "",
              newsletter_opt_in: false
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              subject: {
                required: true
              },
              message: {
                required: true
              }
            }} submitText="Send Message" successMessage="Thank you! We've received your message and will get back to you shortly.">
                <ContactForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}